import { Dialog } from 'primereact/dialog'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import EmptyState from '../../../components/ui/EmptyState'
import LoadingSkeleton from '../../../components/ui/LoadingSkeleton'
import HistoryTimeline, { type TimelineEvent } from '../../../components/ui/HistoryTimeline'
import { formatDateTime } from '../../../lib/datetime'
import { useMeetingLogs } from '../hooks/useMeetingLogs'
import type { MeetingLogDetail, MeetingLogEntry } from '../types'

interface Props {
  meetingId: number
  visible: boolean
  onHide: () => void
}

/**
 * İşlem Geçmişi (046) — bir tutanağın denetim kayıtlarını zaman tüneli olarak gösterir (en yeni önce).
 * Her olay: işlem adı (başlık) + kullanıcı·zaman (meta) + LOG_INFO'dan çözülmüş değişiklik satırları
 * ("Ad: eski → yeni"). Paylaşılan {@link HistoryTimeline} kullanılır (Reuse Mandate). Veri yalnız dialog
 * açıkken çekilir.
 */
export default function MeetingHistoryDialog({ meetingId, visible, onHide }: Readonly<Props>) {
  const { t } = useTranslation()
  const { data, isLoading, isError } = useMeetingLogs(meetingId, visible)

  const events: TimelineEvent[] = (data ?? []).map((entry, index) => ({
    title: entry.operationName,
    meta: [actorLabel(entry, t), formatDateTime(entry.creDate)].filter(Boolean).join(' · '),
    details: entry.details.length > 0 ? <DetailLines entry={entry} /> : undefined,
    // Liste en-yeni-önce → ilk kayıt en son eklenen işlem; yeşil vurguyla ayrışır.
    highlight: index === 0,
  }))

  return (
    <Dialog
      visible={visible}
      onHide={onHide}
      header={t('meetings.detail.historyDialog.title')}
      modal
      dismissableMask
      style={{ width: '40rem', maxWidth: '95vw' }}
      className="mtg-history-dialog"
    >
      {isLoading && <LoadingSkeleton lines={4} />}
      {!isLoading && isError && (
        <EmptyState
          icon="pi pi-exclamation-triangle"
          title={t('meetings.detail.historyDialog.loadError')}
        />
      )}
      {!isLoading && !isError && events.length === 0 && (
        <EmptyState icon="pi pi-history" title={t('meetings.detail.historyDialog.empty')} />
      )}
      {!isLoading && !isError && events.length > 0 && <HistoryTimeline events={events} />}
    </Dialog>
  )
}

/** Bir olayın LOG_INFO değişiklik satırları: "Alan: eski → yeni" ya da özet "Alan: değer". */
function DetailLines({ entry }: Readonly<{ entry: MeetingLogEntry }>) {
  const { t } = useTranslation()
  const label = (key: string) =>
    t(`meetings.detail.historyDialog.field.${key}`, { defaultValue: key })
  return (
    <ul className="mtg-history-changes">
      {entry.details.map((d: MeetingLogDetail, i) => (
        <li key={i}>
          <span className="mtg-history-field">{label(d.key)}:</span>{' '}
          {d.oldValue != null ? (
            <span>
              <span className="mtg-history-old">{d.oldValue}</span>
              {' '}
              <span className="mtg-history-arrow">{t('meetings.detail.historyDialog.changeTo')}</span>
              {' '}
              <span className="mtg-history-new">{d.newValue ?? '—'}</span>
            </span>
          ) : (
            <span className="mtg-history-new">{d.newValue ?? '—'}</span>
          )}
        </li>
      ))}
    </ul>
  )
}

/**
 * Meta satırındaki kişi etiketi. Vekil olarak yapılmış bir işlemde tek bir ad yetmez: kaydın sahibi
 * (adına çalışılan) ile işlemi fiilen yapan farklıdır ve denetim izinin değeri tam olarak bu ayrımdadır
 * (089). Vekil bilgisi yoksa bugünkü metin birebir korunur.
 */
function actorLabel(entry: MeetingLogEntry, t: TFunction): string | null {
  if (!entry.actingUserName) return entry.userName
  return t('proxy.history.onBehalf', { owner: entry.userName ?? '', actor: entry.actingUserName })
}
